import React from 'react';
import { Check, Edit3 } from 'lucide-react';
import { AtomicStep, FieldDefinition } from '../../types';
import { ValidationError } from '../../services/engine';

interface DataFormProps {
  step: AtomicStep;
  fieldValues: Record<string, any>;
  errors: ValidationError[];
  onChange: (fieldId: string, value: any) => void;
}

export const DataForm: React.FC<DataFormProps> = ({
  step,
  fieldValues,
  errors,
  onChange,
}) => {
  const getFieldError = (fieldId: string) => {
    return errors.find(err => err.fieldId === fieldId)?.message;
  };

  const isSitelinerBypassed =
    step.id === 'step-siteliner' &&
    (fieldValues.sitelinerAccessStatus === 'blocked_bypass' ||
      fieldValues.sitelinerAccessStatus === 'unreachable_bypass');

  const handleMultiSelectToggle = (fieldId: string, optionValue: string, currentVal: any) => {
    let currentArray: string[] = [];

    if (Array.isArray(currentVal)) {
      currentArray = [...currentVal];
    } else if (typeof currentVal === 'string' && currentVal.trim().length > 0) {
      currentArray = currentVal.split(',').map(s => s.trim());
    }

    if (optionValue === 'None') {
      onChange(fieldId, ['None']);
      return;
    }

    // Remove 'None' if another option is chosen
    currentArray = currentArray.filter(v => v !== 'None');

    if (currentArray.includes(optionValue)) {
      currentArray = currentArray.filter(v => v !== optionValue);
    } else {
      currentArray.push(optionValue);
    }

    onChange(fieldId, currentArray);
  };

  return (
    <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>
        <Edit3 size={14} />
        <span>What Should You Record? — Actionable Inputs</span>
      </div>

      {isSitelinerBypassed && (
        <div
          style={{
            background: 'rgba(2, 132, 199, 0.12)',
            border: '1px solid rgba(56, 189, 248, 0.3)',
            borderRadius: '8px',
            padding: '12px 16px',
            color: '#38bdf8',
            fontSize: '13px',
            marginBottom: '20px',
            lineHeight: 1.5,
          }}
        >
          <strong>ℹ️ Step Bypassed:</strong> Siteliner scan is marked as Bypassed due to client server/firewall restrictions. Percentage fields and evidence requirements are bypassed so you can proceed directly to the next step.
        </div>
      )}

      {step.fields.map((field: FieldDefinition) => {
        if (
          isSitelinerBypassed &&
          (field.id === 'duplicateContentPct' ||
            field.id === 'commonContentPct' ||
            field.id === 'uniqueContentPct')
        ) {
          return null;
        }

        const errorMsg = getFieldError(field.id);
        const val = fieldValues[field.id] ?? '';

        return (
          <div key={field.id} style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', display: 'block', marginBottom: '6px' }} htmlFor={field.id}>
              {field.label} {field.required && !isSitelinerBypassed && <span style={{ color: '#ef4444' }}>*</span>}
            </label>

            {field.type === 'multiselect' ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '8px' }}>
                {field.options?.map(opt => {
                  let isSelected = false;
                  if (Array.isArray(val)) {
                    isSelected = val.includes(opt.value);
                  } else if (typeof val === 'string') {
                    isSelected = val.split(',').map(s => s.trim()).includes(opt.value);
                  }

                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleMultiSelectToggle(field.id, opt.value, val)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 14px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        border: isSelected ? '1px solid #38bdf8' : '1px solid #1e293b',
                        background: isSelected ? 'rgba(2, 132, 199, 0.25)' : '#070a12',
                        color: isSelected ? '#ffffff' : '#94a3b8',
                      }}
                    >
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '4px',
                          border: isSelected ? 'none' : '1px solid #64748b',
                          background: isSelected ? '#38bdf8' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {isSelected && <Check size={12} color="#000000" />}
                      </div>
                      <span>{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            ) : field.type === 'select' ? (
              <select
                id={field.id}
                className="input-field"
                style={{
                  width: '100%',
                  background: '#070a12',
                  border: errorMsg ? '1px solid #ef4444' : '1px solid #1e293b',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  color: '#ffffff',
                  fontSize: '13px',
                  outline: 'none',
                }}
                value={val}
                onChange={(e) => onChange(field.id, e.target.value)}
              >
                <option value="">-- Select Option --</option>
                {field.options?.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : field.type === 'text' ? (
              <textarea
                id={field.id}
                className="input-field"
                style={{
                  width: '100%',
                  background: '#070a12',
                  border: errorMsg ? '1px solid #ef4444' : '1px solid #1e293b',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  color: '#ffffff',
                  fontSize: '13px',
                  outline: 'none',
                  minHeight: '90px',
                  resize: 'vertical',
                }}
                value={val}
                placeholder={field.placeholder}
                onChange={(e) => onChange(field.id, e.target.value)}
              />
            ) : (
              <input
                id={field.id}
                type={field.type === 'integer' || field.type === 'decimal' ? 'number' : 'text'}
                step={field.type === 'decimal' ? '0.01' : '1'}
                className="input-field"
                style={{
                  width: '100%',
                  background: '#070a12',
                  border: errorMsg ? '1px solid #ef4444' : '1px solid #1e293b',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  color: '#ffffff',
                  fontSize: '13px',
                  outline: 'none',
                }}
                value={val}
                placeholder={field.placeholder}
                onChange={(e) => onChange(field.id, e.target.value)}
              />
            )}

            {field.helpText && (
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                {field.helpText}
              </div>
            )}

            {errorMsg && <div style={{ fontSize: '12px', color: '#ef4444', fontWeight: 600, marginTop: '4px' }}>{errorMsg}</div>}
          </div>
        );
      })}
    </div>
  );
};
